import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { RefreshCw } from 'lucide-react';
import { Button, Badge, Modal, Input, Select } from '@/components/ui';
import { appointmentService } from '@/services/appointmentService';
import { resourceService } from '@/services/resourceService';
import type { Appointment, AppointmentStatus } from '@/types/appointment';
import type { Resource } from '@/types';
import styles from './DailyPanelPage.module.css';

const AUTO_REFRESH_INTERVAL_MS = 30_000;

const STATUS_LABELS: Record<AppointmentStatus, string> = {
  SCHEDULED: 'Agendado',
  CONFIRMED: 'Confirmado',
  WAITING: 'Aguardando',
  IN_PROGRESS: 'Em Atendimento',
  COMPLETED: 'Concluído',
  CANCELLED: 'Cancelado',
  NO_SHOW: 'Não Compareceu',
};

const BADGE_VARIANT_MAP: Record<AppointmentStatus, 'info' | 'success' | 'warning' | 'error' | 'neutral'> = {
  SCHEDULED: 'info',
  CONFIRMED: 'success',
  WAITING: 'warning',
  IN_PROGRESS: 'info',
  COMPLETED: 'neutral',
  CANCELLED: 'error',
  NO_SHOW: 'error',
};

/**
 * Returns today's date as YYYY-MM-DD string.
 */
function getTodayString(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Daily operations panel for the clinic.
 *
 * Features:
 * - Dashboard cards: Total Scheduled, Confirmed, Completed, No-Shows
 * - Ordered list of today's appointments by time
 * - Each row: time, patient name, procedure, resource, status badge
 * - Quick action buttons: Confirm, Check-in, Start, Complete, No-show, Cancel
 * - Auto-refresh every 30 seconds
 * - Filter by resource
 *
 * Accessibility:
 * - aria-live on auto-refresh indicator
 * - role="alert" on error states
 * - Semantic HTML structure
 * - Keyboard-accessible action buttons
 */
export function DailyPanelPage() {
  const queryClient = useQueryClient();
  const today = getTodayString();

  const [resourceFilter, setResourceFilter] = useState('');
  const [cancellingAppointmentId, setCancellingAppointmentId] = useState<string | null>(null);
  const [cancelReason, setCancelReason] = useState('');
  const autoRefreshRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Fetch clinic dashboard
  const {
    data: dashboard,
    isLoading,
    error: queryError,
    refetch,
  } = useQuery({
    queryKey: ['clinic-dashboard', today],
    queryFn: () => appointmentService.getClinicDashboard(today),
    refetchInterval: AUTO_REFRESH_INTERVAL_MS,
  });

  // Fetch resources for filter
  const { data: resources = [] } = useQuery({
    queryKey: ['resources-active-panel'],
    queryFn: () => resourceService.getResources({ isActive: true }),
  });

  const resourceOptions = useMemo(
    () => [
      { value: '', label: 'Todos os recursos' },
      ...resources.map((r: Resource) => ({ value: r.id, label: r.name })),
    ],
    [resources],
  );

  // Filtered and sorted appointments
  const filteredAppointments = useMemo(() => {
    if (!dashboard) return [];
    let appointments = [...(dashboard.appointments ?? [])];
    if (resourceFilter) {
      appointments = appointments.filter((a) => a.resourceId === resourceFilter);
    }
    appointments.sort((a, b) => a.startTime.localeCompare(b.startTime));
    return appointments;
  }, [dashboard, resourceFilter]);

  // Cleanup auto-refresh on unmount
  useEffect(() => {
    const intervalRef = autoRefreshRef.current;
    return () => {
      if (intervalRef) {
        clearInterval(intervalRef);
      }
    };
  }, []);

  // Status transition mutations
  const confirmMutation = useMutation({
    mutationFn: (id: string) => appointmentService.confirm(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clinic-dashboard'] });
    },
  });

  const checkInMutation = useMutation({
    mutationFn: (id: string) => appointmentService.checkIn(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clinic-dashboard'] });
    },
  });

  const startMutation = useMutation({
    mutationFn: (id: string) => appointmentService.start(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clinic-dashboard'] });
    },
  });

  const completeMutation = useMutation({
    mutationFn: (id: string) => appointmentService.complete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clinic-dashboard'] });
    },
  });

  const noShowMutation = useMutation({
    mutationFn: (id: string) => appointmentService.markNoShow(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clinic-dashboard'] });
    },
  });

  const cancelMutation = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      appointmentService.cancel(id, { reason }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clinic-dashboard'] });
      setCancellingAppointmentId(null);
      setCancelReason('');
    },
  });

  const handleCancelSubmit = useCallback(
    (event: React.FormEvent) => {
      event.preventDefault();
      if (!cancellingAppointmentId || !cancelReason.trim()) return;
      cancelMutation.mutate({ id: cancellingAppointmentId, reason: cancelReason.trim() });
    },
    [cancellingAppointmentId, cancelReason, cancelMutation],
  );

  const handleManualRefresh = useCallback(() => {
    refetch();
  }, [refetch]);

  /**
   * Renders quick action buttons based on the current appointment status.
   */
  const renderQuickActions = (appointment: Appointment) => {
    const isPending =
      confirmMutation.isPending ||
      checkInMutation.isPending ||
      startMutation.isPending ||
      completeMutation.isPending ||
      noShowMutation.isPending;

    switch (appointment.status) {
      case 'SCHEDULED':
        return (
          <>
            <Button
              variant="primary"
              size="sm"
              onClick={() => confirmMutation.mutate(appointment.id)}
              disabled={isPending}
              isLoading={confirmMutation.isPending}
            >
              Confirmar
            </Button>
            <Button
              variant="danger"
              size="sm"
              onClick={() => {
                setCancellingAppointmentId(appointment.id);
                setCancelReason('');
              }}
              disabled={isPending}
            >
              Cancelar
            </Button>
          </>
        );
      case 'CONFIRMED':
        return (
          <>
            <Button
              variant="primary"
              size="sm"
              onClick={() => checkInMutation.mutate(appointment.id)}
              disabled={isPending}
              isLoading={checkInMutation.isPending}
            >
              Check-in
            </Button>
            <Button
              variant="danger"
              size="sm"
              onClick={() => {
                setCancellingAppointmentId(appointment.id);
                setCancelReason('');
              }}
              disabled={isPending}
            >
              Cancelar
            </Button>
          </>
        );
      case 'WAITING':
        return (
          <>
            <Button
              variant="primary"
              size="sm"
              onClick={() => startMutation.mutate(appointment.id)}
              disabled={isPending}
              isLoading={startMutation.isPending}
            >
              Iniciar
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => noShowMutation.mutate(appointment.id)}
              disabled={isPending}
              isLoading={noShowMutation.isPending}
            >
              Não Compareceu
            </Button>
          </>
        );
      case 'IN_PROGRESS':
        return (
          <Button
            variant="primary"
            size="sm"
            onClick={() => completeMutation.mutate(appointment.id)}
            disabled={isPending}
            isLoading={completeMutation.isPending}
          >
            Concluir
          </Button>
        );
      default:
        return null;
    }
  };

  if (queryError) {
    return (
      <div className={styles.page}>
        <div className={styles.errorState} role="alert">
          <p>Erro ao carregar painel do dia. Tente novamente mais tarde.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div>
          <h1 className={styles.title}>Painel do Dia</h1>
          <p className={styles.subtitle}>
            Acompanhe em tempo real os agendamentos de hoje.
          </p>
        </div>
        <Button
          variant="outline"
          leftIcon={<RefreshCw size={16} />}
          onClick={handleManualRefresh}
          aria-label="Atualizar painel"
        >
          Atualizar
        </Button>
      </header>

      {/* Dashboard cards */}
      {dashboard && (
        <section
          className={styles.dashboardCards}
          aria-label="Resumo do dia"
        >
          <div className={[styles.card, styles.cardScheduled].join(' ')}>
            <span className={styles.cardLabel}>Agendados</span>
            <span className={styles.cardValue} aria-live="polite">
              {dashboard.totalScheduled}
            </span>
          </div>
          <div className={[styles.card, styles.cardConfirmed].join(' ')}>
            <span className={styles.cardLabel}>Confirmados</span>
            <span className={styles.cardValue} aria-live="polite">
              {dashboard.totalConfirmed}
            </span>
          </div>
          <div className={[styles.card, styles.cardCompleted].join(' ')}>
            <span className={styles.cardLabel}>Concluídos</span>
            <span className={styles.cardValue} aria-live="polite">
              {dashboard.totalCompleted}
            </span>
          </div>
          <div className={[styles.card, styles.cardNoShow].join(' ')}>
            <span className={styles.cardLabel}>Não Compareceram</span>
            <span className={styles.cardValue} aria-live="polite">
              {dashboard.totalNoShow}
            </span>
          </div>
        </section>
      )}

      {/* Filters */}
      <div className={styles.filters}>
        <div className={styles.filterRow}>
          <div className={styles.filterResource}>
            <Select
              label="Filtrar por recurso"
              options={resourceOptions}
              value={resourceFilter}
              onChange={(e) => setResourceFilter(e.target.value)}
              fullWidth
            />
          </div>
          <div className={styles.refreshIndicator} aria-live="polite">
            <span className={styles.refreshDot} aria-hidden="true" />
            <span>Atualização automática a cada 30s</span>
          </div>
        </div>
      </div>

      {/* Appointments list */}
      {isLoading ? (
        <div className={styles.loadingState} role="status" aria-busy="true">
          <p>Carregando painel...</p>
        </div>
      ) : filteredAppointments.length === 0 ? (
        <div className={styles.emptyState}>
          <p>Nenhum agendamento para hoje{resourceFilter ? ' com este recurso' : ''}.</p>
        </div>
      ) : (
        <section
          className={styles.appointmentList}
          aria-label="Lista de agendamentos do dia"
        >
          {filteredAppointments.map((appointment) => (
            <article
              key={appointment.id}
              className={styles.appointmentRow}
              aria-label={`${appointment.patientName}, ${appointment.startTime} - ${appointment.endTime}`}
            >
              <div className={styles.appointmentRowTop}>
                <span className={styles.appointmentTime}>
                  {appointment.startTime} - {appointment.endTime}
                </span>
                <div className={styles.appointmentDetails}>
                  <span className={styles.appointmentPatient}>{appointment.patientName}</span>
                  <span className={styles.appointmentMeta}>
                    {appointment.procedureTypeName} &middot; {appointment.resourceName}
                  </span>
                </div>
                <span className={styles.appointmentStatus}>
                  <Badge variant={BADGE_VARIANT_MAP[appointment.status]}>
                    {STATUS_LABELS[appointment.status]}
                  </Badge>
                </span>
              </div>
              {/* Quick actions - only for actionable statuses */}
              {['SCHEDULED', 'CONFIRMED', 'WAITING', 'IN_PROGRESS'].includes(
                appointment.status,
              ) && (
                <div className={styles.quickActions}>{renderQuickActions(appointment)}</div>
              )}
            </article>
          ))}
        </section>
      )}

      {/* Cancel confirmation modal */}
      <Modal
        isOpen={cancellingAppointmentId !== null}
        onClose={() => setCancellingAppointmentId(null)}
        title="Cancelar Agendamento"
        size="sm"
      >
        <form onSubmit={handleCancelSubmit} className={styles.cancelForm} noValidate>
          <Input
            label="Motivo do cancelamento"
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            placeholder="Informe o motivo"
            required
            fullWidth
          />
          <div className={styles.cancelFormActions}>
            <Button
              variant="ghost"
              type="button"
              onClick={() => setCancellingAppointmentId(null)}
            >
              Voltar
            </Button>
            <Button
              variant="danger"
              type="submit"
              disabled={!cancelReason.trim()}
              isLoading={cancelMutation.isPending}
            >
              Confirmar Cancelamento
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
